import { useEffect } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { withAuthUser } from "next-firebase-auth";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useAuth } from "@/components/auth/hooks/useAuth";
import { useTranslation } from "next-i18next";
// BOOTSTRAP
import Col from "react-bootstrap/Col";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
// COMPONENTS
import BootstrapSpinner from "@/components/elements/LoadingPlaceholders/BootstrapSpinner";
import SideLinks from "@/components/core/CustomerService/SideLinks/SideLinks";
// ICONS
import { CreditCardIcon, PackageIcon, PhoneIcon, RefreshCcwIcon } from "@/public/icons/icons";
// STYLE
import s from "./customer-service.module.scss";
import { NextSeo } from "next-seo";

const customButtonLink = [
  {
    id: 0,
    title: "customButtonLink-text1",
    href: "/delivery",
    icon: <PackageIcon width="48" height="48" color="white" strokeWidth="1" />,
  },
  {
    id: 1,
    title: "customButtonLink-text2",
    href: "/payments",
    icon: <CreditCardIcon width="44" height="44" color="white" strokeWidth="1.2" />,
  },
  {
    id: 2,
    title: "customButtonLink-text3",
    href: "/returns",
    icon: <RefreshCcwIcon width="44" height="44" color="white" strokeWidth="1.2" />,
  },
  {
    id: 3,
    title: "customButtonLink-text4",
    href: "/contact",
    icon: <PhoneIcon width="44" height="44" color="white" strokeWidth="1.2" />,
  },
];

interface CustomButton {
  href: string;
  text: string;
  icon: JSX.Element;
}

const CustomButton = (props: CustomButton) => {
  return (
    <div className={s.buttonStyle}>
      <Link href={props.href} passHref>
        <div className={s.containerStyle}>
          <Row
            className={`mx-0 ${s.buttonStyle}`}
            style={{
              border: "1px solid black",
              backgroundColor: "white",
              cursor: "pointer",
              height: "3.5rem",
            }}
          >
            <Col
              xs="2"
              className={s.darkCol}
              style={{
                alignItems: "center",
                backgroundColor: "black",
                display: "flex",
                cursor: "pointer",
                height: "3.4rem",
                justifyContent: "center",
              }}
            >
              {props.icon}
            </Col>

            <Col xs="10" className={s.lightCol} style={{ height: "3.4rem", display: "flex", alignItems: "center" }}>
              <div className="mx-3" style={{ display: "flex", alignItems: "center" }}>
                <h5 className="mb-0 fs-5 fw-semibold">{props.text}</h5>
              </div>
            </Col>
          </Row>
        </div>
      </Link>
    </div>
  );
};

function CustomerService() {
  const auth = useAuth();
  const router = useRouter();

  const { t } = useTranslation("customer-service");

  // If the user is not allowed will be redirect to homepage.
  useEffect(() => {
    if (auth.isUserSupplier()) {
      router.push("/home-supplier");
    }
  }),
    [auth];

  return auth.isUserSupplier() ? (
    <BootstrapSpinner />
  ) : (
    <Container fluid className="px-0">
      <NextSeo
        title={t("customer-service")}
        description={""}
        openGraph={{
          type: "website",
          title: t("customer-service"),
          description: "",
          url: `${process.env.NEXT_PUBLIC_BASE_URL}/customer-service`,
        }}
      />
      <Row className="mx-0 px-3">
        <Col className={`${s.side_links}`}>
          <SideLinks />
        </Col>
        <Col className="px-0">
          <h1 className="fs-1 fw-semibold text-center text-md-start">{t("title1").toUpperCase()}</h1>
          <p>{t("subtitle1")}</p>
          <div className="pt-5">
            <Link href="/find-us" passHref>
              <a className="fw-semibold text-decoration-underline pb-2">{t("link1")}</a>
            </Link>
            <p>{t("subtitle-link1")}</p>
          </div>
          <div className="pt-5">
            <span className="fw-semibold">{t("title2")}</span>
            <div className="py-2">
              {customButtonLink.map((item, index) => (
                <div className="py-2" key={index}>
                  <CustomButton icon={item.icon} href={item.href} text={t(item.title)} />
                </div>
              ))}
            </div>
          </div>
          <div className="pt-5 ">
            <p className="d-flex flex-column pb-3">
              <Link href="/about-us" passHref>
                <a className="fw-semibold text-decoration-underline  py-1">{t("about-us")}</a>
              </Link>
              <span className="fw-medium pb-2">{t("useful-informations")}</span>
            </p>
            <p className="d-flex flex-column pb-3">
              <Link href="/privacy-and-cookie" passHref>
                <a className="fw-semibold text-decoration-underline  py-1">{t("privacy-policy")}</a>
              </Link>
              <Link href="/terms-and-conditions" passHref>
                <a className="fw-semibold text-decoration-underline  py-1">{t("terms-conditions")}</a>
              </Link>
            </p>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ["common", "header", "footer", "customer-service", "services-side-links", "db-translations"])),
    },
  };
}

export default withAuthUser({})(CustomerService);
